import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import moment from "moment";

import { useI18n } from "Hooks";
import {
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Modal,
  Button,
  TextAreaInput,
  SelectDropdown,
} from "@toddle-edu/ds-web";
import { WarningTriangleFilled } from "@toddle-edu/ds-icons";

import {
  ALERT_TYPES,
  getAlertTypeOptions,
  getSelectedStudentsForBulkReminder,
} from "ClassRoom/modules/utils";
import { createAssignmentBulkAlertMessage } from "ClassRoom/modules/ClassRoomModule";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import ProfileCascade from "UIComponents/ProfileCascade";

import classes from "./SendAlertMessageModal.scss";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getDateTimeDifference } from "Utils/DateTimeHelpers";

const styles = {
  avatarStyles: {
    size: "small",
    shape: "circle",
    iconType: "individual",
    shade: "light",
    randomizeColor: true,
  },
};

const getFormattedStudentOptions = studentsSubmissions => {
  const students = _.map(
    studentsSubmissions,
    assignedStudent => {
      const studentData = _.get(assignedStudent, "student", {});
      const studentId = _.get(studentData, "id", null);
      const profileImage = _.get(studentData, "profileImage", null);
      return {
        label: getFormattedUserName({ user: studentData }),
        value: studentId,
        icon: (
          <ProfileCascade
            items={[
              {
                id: studentId,
                value: getFormattedUserName({ user: studentData }),
                imageUrl: profileImage,
              },
            ]}
            showAvatar={true}
            avatarStyles={styles.avatarStyles}
          />
        ),
      };
    },
    []
  );
  return students;
};

const getInitialAlertModalData = ({
  sortedAssignedStudents,
  selectedStudentsSubmissions,
  modalType,
  t,
  assignmentDeadline,
  studentId,
  isStudentSubmissionEnabled,
  isBackdateAssignment,
}) => {
  const alertTypeOptions = getAlertTypeOptions({
    t,
    assignmentDeadline,
    isStudentSubmissionEnabled,
    sortedAssignedStudents,
    isBackdateAssignment,
  });
  let sendAlertTo = [];
  let alertTypeOption;
  if (
    _.first(alertTypeOptions).key ===
      ALERT_TYPES.ASSIGNMENT_DUE_DATE_APPROACHING_ALERT &&
    getDateTimeDifference({
      date1: assignmentDeadline,
      date2: moment(),
    }) > 1
  ) {
    alertTypeOption = alertTypeOptions[1];
  } else {
    alertTypeOption = _.first(alertTypeOptions);
  }

  if (modalType === "reminder") {
    if (
      alertTypeOption.key === ALERT_TYPES.ASSIGNMENT_CUSTOM_ALERT &&
      _.size(alertTypeOptions) === 1
    ) {
      sendAlertTo = [];
    } else {
      const selectedStudents = getSelectedStudentsForBulkReminder(
        sortedAssignedStudents
      );
      sendAlertTo = getFormattedStudentOptions(selectedStudents);
    }
  } else {
    if (studentId) {
      const selectedStudent = _.find(
        sortedAssignedStudents,
        student => _.get(student, "student.id", null) === studentId
      );
      sendAlertTo = getFormattedStudentOptions([selectedStudent]);
    } else if (_.size(selectedStudentsSubmissions) > 0) {
      sendAlertTo = getFormattedStudentOptions(selectedStudentsSubmissions);
    } else {
      sendAlertTo = getFormattedStudentOptions(sortedAssignedStudents);
    }
  }

  return {
    sendAlertTo,
    alertType: modalType === "reminder" ? alertTypeOption.key : null,
    message:
      modalType === "reminder"
        ? _.get(alertTypeOption, "defaultMessage", "")
        : "",
  };
};

const SendAlertMessageModal = ({
  createAssignmentBulkAlertMessage,
  selectedStudentsSubmissions,
  sendAlertMessageModalParams,
  setSendAlertMessageModalParams,
  sortedAssignedStudents,
  assignmentDeadline,
  assignmentId,
  isStudentSubmissionEnabled,
  isBackdateAssignment = false,
}) => {
  const { t } = useI18n();
  const modalType = _.get(sendAlertMessageModalParams, "type", null);
  const studentId = _.get(sendAlertMessageModalParams, "studentId", null);

  const [sendAlertMessageModalData, setSendAlertMessageModalData] = useState(
    () =>
      getInitialAlertModalData({
        sortedAssignedStudents,
        selectedStudentsSubmissions,
        modalType,
        t,
        assignmentDeadline,
        studentId,
        isStudentSubmissionEnabled,
        isBackdateAssignment,
      })
  );
  const [errors, setErrors] = useState({
    isSendAlertToEmpty: false,
    isMessageEmpty: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { sendAlertTo, alertType, message } = sendAlertMessageModalData;
  const { isSendAlertToEmpty, isMessageEmpty } = errors;
  const alertTypeOptions = getAlertTypeOptions({
    t,
    assignmentDeadline,
    isStudentSubmissionEnabled,
    sortedAssignedStudents,
    isBackdateAssignment,
  });
  const alertTypeObj = _.find(alertTypeOptions, item => item.key === alertType);

  const handleSendMessageClick = async () => {
    const trimmedMessage = _.trim(message);
    if (
      (modalType === "reminder" && _.isEmpty(sendAlertTo)) ||
      !trimmedMessage
    ) {
      setErrors({
        isSendAlertToEmpty: _.isEmpty(sendAlertTo),
        isMessageEmpty: _.isEmpty(trimmedMessage),
      });
      return;
    }
    setIsLoading(true);
    const studentIds = _.map(sendAlertTo, students =>
      _.get(students, "value", null)
    );

    const variables = {
      input: [
        {
          assignmentId,
          studentIds,
          alertType: modalType === "reminder" ? alertType : "TEXT_ONLY",
          message: trimmedMessage,
        },
      ],
    };

    await createAssignmentBulkAlertMessage({
      variables,
      sendAlertTo,
      modalType,
    });
    setIsLoading(false);
    setSendAlertMessageModalParams({});
  };

  const handleSelectDropdownChange = value => {
    const newSendAlertMessageModalData = { ...sendAlertMessageModalData };
    newSendAlertMessageModalData["sendAlertTo"] = value;
    if (isSendAlertToEmpty) {
      setErrors({ ...errors, isSendAlertToEmpty: false });
    }
    setSendAlertMessageModalData(newSendAlertMessageModalData);
  };

  const handleAlertTypeDropdownChange = value => {
    const newSendAlertMessageModalData = { ...sendAlertMessageModalData };
    const alertTypeOption =
      _.find(
        alertTypeOptions,
        alertTypes => _.get(alertTypes, "key", "") === _.get(value, "key", "")
      ) || {};
    newSendAlertMessageModalData["alertType"] = alertTypeOption.key;
    newSendAlertMessageModalData["message"] = _.get(
      alertTypeOption,
      "defaultMessage",
      ""
    );
    if (alertTypeOption.key === ALERT_TYPES.ASSIGNMENT_CUSTOM_ALERT) {
      newSendAlertMessageModalData["sendAlertTo"] = [];
    } else {
      const selectedStudents = getSelectedStudentsForBulkReminder(
        sortedAssignedStudents
      );
      newSendAlertMessageModalData["sendAlertTo"] = getFormattedStudentOptions(
        selectedStudents
      );
    }
    setSendAlertMessageModalData(newSendAlertMessageModalData);
  };

  const handleTextAreaInput = e => {
    const newSendAlertMessageModalData = { ...sendAlertMessageModalData };
    newSendAlertMessageModalData["message"] = e?.target?.value;
    if (isMessageEmpty) {
      setErrors({ ...errors, isMessageEmpty: false });
    }
    setSendAlertMessageModalData(newSendAlertMessageModalData);
  };

  const handleClose = () => {
    setSendAlertMessageModalParams({});
  };

  const renderErrorMsg = errorMsg => {
    const errorContainerClasses = classNames(
      "text-body-3",
      classes.errorContainer
    );
    return (
      <span className={errorContainerClasses}>
        <span className={classes.iconContainer}>
          <WarningTriangleFilled variant="critical" size="xxx-small" />
        </span>
        <span className={classes.errorMsgContainer}>{errorMsg}</span>
      </span>
    );
  };

  const modalTitle =
    modalType === "reminder"
      ? t("common:send_a_reminder")
      : _.size(sendAlertTo) === 1
      ? t("common:send_message_to_student", {
          name: _.get(sendAlertTo[0], "label", ""),
        })
      : t("common:send_broadcast_message_to_students", {
          count: _.size(sendAlertTo),
        });

  const modalSubtitle =
    modalType === "reminder"
      ? t("classRoom:send_alert_message_subtext_new")
      : null;

  const sendButtonTitle =
    modalType === "reminder"
      ? t("common:send_reminder")
      : t("common:send_message");

  const messagePlaceHolder =
    modalType === "reminder"
      ? t("common:type_your_custom_message_here")
      : t("common:type_your_message_here");

  return (
    <>
      {isLoading ? <FullScreenLoader /> : null}
      <Modal width="592px" height="max-content" onClose={handleClose}>
        <Modal.Header title={modalTitle} subtitle={modalSubtitle} />
        <Modal.Body isScrollable>
          <div className={classes.bodyContainer}>
            {modalType === "reminder" ? (
              <>
                <div className={classes.selectFieldContainer}>
                  <div className="text-label-2">
                    {t("classRoom:reminder_type")}
                  </div>
                  <Dropdown
                    overlay={
                      <DropdownMenu
                        onClick={handleAlertTypeDropdownChange}
                        options={alertTypeOptions}
                        showSelection={true}
                        selectionType="tick"
                        activeKey={alertType}
                      />
                    }
                  >
                    <ButtonDisclosure
                      className={classes.alertTypeDropdown}
                      _legacy_variant="outlined-subtle"
                      variant="neutral"
                      type="outlined"
                    >
                      <span className={classes.alertTypeLabel}>
                        {_.get(alertTypeObj, "label", "")}
                      </span>
                    </ButtonDisclosure>
                  </Dropdown>
                </div>
                <div className={classes.selectFieldContainer}>
                  <div className="text-label-2">
                    {t("common:mandatory_label", {
                      label: t("classRoom:students"),
                    })}
                  </div>
                  <div>
                    <SelectDropdown
                      value={sendAlertTo}
                      options={getFormattedStudentOptions(
                        sortedAssignedStudents
                      )}
                      placeholder={t("common:select_students")}
                      onChange={handleSelectDropdownChange}
                      isSearchEnabled={true}
                      isMulti={true}
                      size="medium"
                      isClearable={false}
                      error={isSendAlertToEmpty}
                      showOverflowingTagsCount={true}
                      showCheckbox={true}
                    />
                    {isSendAlertToEmpty
                      ? renderErrorMsg(t("classRoom:please_select_a_student"))
                      : null}
                  </div>
                </div>
              </>
            ) : null}
            <div>
              <TextAreaInput
                label={t("common:mandatory_label", {
                  label: t("common:message"),
                })}
                placeholder={messagePlaceHolder}
                value={message}
                onChange={handleTextAreaInput}
                error={isMessageEmpty}
              />
              {isMessageEmpty
                ? renderErrorMsg(t("classRoom:please_add_a_message"))
                : null}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footerContainer}>
            <Button
              size="large"
              variant="outlined-subtle"
              onClick={handleClose}
            >
              {t("common:cancel")}
            </Button>
            <Button
              size="large"
              variant="progressive"
              onClick={handleSendMessageClick}
            >
              {sendButtonTitle}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapActionCreators = {
  createAssignmentBulkAlertMessage,
};

export default connect(null, mapActionCreators)(SendAlertMessageModal);
